import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NodeSetTypes, SourceTypes } from "@common/types/server-list-types";
import { AuthContext } from "@src/auth/use-auth-context";
import { BlueprintsArrayObjectTypes } from "@common/types/blueprint-types";
import { LicensedConfigurationResponse } from "@common/types/edge-client-configuration-types";
import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import {
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
  transformedGatewaysWithMachinesListEntryTypes,
} from "@common/types/machines-list-types";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { DeviceSecurityMode } from "@src/types/device-security-mode";
import { DeviceSecurityPolicy } from "@src/types/device-security-policy";
import {
  UserManagementListActiveViewType,
  ZitadelServiceUser,
  ZitadelUser,
} from "@src/types/user-management";
import { RolesDescriptions } from "@src/types/role-manager";
import { Route } from "@src/routes/routes";
import {
  supportTicketByIdType,
  GetSupportCommentsByTicketResponse,
  TicketCommentAttachment,
} from "@src/types/support";
import { StreamTool } from "@src/components/molecules/tool-activity-section/stream-tools";
import { SessionVisibilityType } from "@src/types/remote-vnc-session";

interface ScanModalTypes {
  showScanModal: boolean;
  scanModalState: string;
}
interface AddMachineGroupsAdminPanelModalTypes {
  showAddMachineGroupsAdminPanelModal: boolean;
}
interface SaveBlueprintModalTypes {
  showSaveBlueprintModal: boolean;
  blueprintModalContentShown: "save" | "replace";
  selectedBlueprint: BlueprintsArrayObjectTypes;
}
interface AutoMatchBlueprintModalTypes {
  showAutoMatchBlueprintModal: boolean;
  autoMatchBlueprintModalContentShown: "find" | "apply";
  selectedBlueprint: BlueprintsArrayObjectTypes;
}
interface MyBlueprintLibraryModalTypes {
  showMyBlueprintLibraryModal: boolean;
  myBlueprintLibraryModalContentShown: "find" | "apply";
  selectedBlueprint: BlueprintsArrayObjectTypes;
}

interface FindBlueprintModalTypes {
  showFindBlueprintModal: boolean;
  blueprintModalContentShown: "find" | "apply";
  selectedFindBlueprint: BlueprintsArrayObjectTypes;
}

interface UnsavedStructureModalTypes {
  showUnsavedStructureModal: boolean;
  routeToNavigateAfterConfirmation: Route | string;
}

interface UnsavedStructureForNodeManagerNavigationModalTypes {
  showUnsavedStructureForNodeManagerNavigationModal: boolean;
  activeViewForNavigation: NodeManagerActiveViewType;
}
interface UnsavedConditionMonitoringForNodeManagerNavigationModalTypes {
  showUnsavedConditionMonitoringForNodeManagerNavigationModal: boolean;
  activeViewForNavigation: NodeManagerActiveViewType;
}

interface FilterModalTypes {
  showFilterModal: boolean;
  filterInputContent: string;
}

interface MqttConnectionErrorTypes {
  error_message: string;
  error_code: number;
  hint: string;
}

interface MqttModalTypes {
  showMqttModal: boolean;
  mqttModalState: MqttModalStateTypes;
  errors: MqttConnectionErrorTypes[];
}

interface MqttConfigurationTypes {
  sink_type: string;
  broker_hostname: string;
  broker_port: number;
  use_tls: boolean;
  mqtt_client_id: string;
  username: string;
  password: string;
  root_topic: string;
  client_certificate_base64: string;
  client_certificate_private_key_base64: string;
  client_certificate_password: string;
  server_public_key_base64: string;
}

interface FirmwareUploadModalTypes {
  showFirmwareUploadModal: boolean;
  firmwareUploadModalState: FirmwareUploadState;
  firmwareUploadModalResponseMessage: string | string[];
}

interface FirmwareInstallModalTypes {
  showFirmwareInstallModal: boolean;
}

interface ConfigurationUploadModalTypes {
  showConfigurationUploadModal: boolean;
  configurationUploadModalState: ConfigurationUploadState;
  configurationUploadModalResponseMessage: string | string[];
}

interface ConfigurationInstallModalTypes {
  showConfigurationInstallModal: boolean;
}

interface OtaDownloadModalTypes {
  showOtaDownloadModal: boolean;
}
interface ConfigurationBackupInstallModalTypes {
  showConfigurationBackupInstallModal: boolean;
}

interface FirmwareBackupInstallModalTypes {
  showFirmwareBackupInstallModal: boolean;
}

interface OtaPushFirmwareModalTypes {
  showOtaPushFirmwareModal: boolean;
}

interface OtaCreateBackupModalTypes {
  showOtaCreateBackupModal: boolean;
}

interface OtaConfirmFirmwareInstallationModalTypes {
  showOtaConfirmFirmwareInstallationModal: boolean;
}

interface OtaConfirmBackupProcessModalTypes {
  showOtaConfirmBackupProcessModal: boolean;
}

interface OtaConfirmFirmwareBackupInstallationModalTypes {
  showOtaConfirmFirmwareBackupInstallationModal: boolean;
}

interface OtaPushFirmwareBackupModalTypes {
  showOtaPushFirmwareBackupModal: boolean;
}

interface OtaAcceptAndPushFirmwareModalTypes {
  showOtaAcceptAndPushFirmwareModal: boolean;
}

interface OtaAcceptAndCreateBackupProcessModalTypes {
  showOtaAcceptAndCreateBackupProcessModal: boolean;
}

interface OtaInstallationInProgressModalTypes {
  showOtaInstallationInProgressModal: boolean;
}

interface OtaInstallationCompleteModalTypes {
  showOtaInstallationCompleteModal: boolean;
}

interface OtaInstallationFailedModalTypes {
  showOtaInstallationFailedModal: boolean;
}

export const emptyMqttConfig = {
  sink_type: "",
  broker_hostname: "",
  broker_port: 0,
  use_tls: false,
  mqtt_client_id: "",
  username: "",
  password: "",
  root_topic: "",
  client_certificate_base64: "",
  client_certificate_private_key_base64: "",
  client_certificate_password: "",
  server_public_key_base64: "",
};

export const emptyClientConfiguration = {
  configuration_type: "",
  version: 0,
  // mqtt_config: emptyMqttConfig,
};

export interface ClientConfigurationTypes {
  configuration_type: string;
  version: number;
  // mqtt_config: MqttConfigurationTypes;
}

export enum ServerTypesConsts {
  OPCUA = "OPC UA",
}

export enum ScanStatesConsts {
  SCAN_STARTED = "scan-started",
  SCAN_COMPLETED = "scan-completed",
  SCAN_FAILED = "scan-failed",
}

export type ConnectionViewStateTypes =
  | "none"
  | "New"
  | "Disconnected"
  | "Connected";

export type MqttModalStateAddConnection = "mqtt-add-connection";
export type MqttModalStateEditConnection = "mqtt-edit-connection";
export type MqttModalStateSuccessfulConnection = "mqtt-successful-connection";
export type MqttModalStateUnsuccessfulConnection =
  "mqtt-unsuccessful-connection";
export type MqttModalStateSpinner = "mqtt-spinner";

export type MqttModalStateTypes =
  | MqttModalStateAddConnection
  | MqttModalStateEditConnection
  | MqttModalStateSuccessfulConnection
  | MqttModalStateUnsuccessfulConnection
  | MqttModalStateSpinner;

export interface ServerConnectionViewTypes {
  source: SourceTypes;
  state: ConnectionViewStateTypes;
  authentication: DeviceAuthOption;
}

export type TreeProps = {
  connected_via_client?: string;
  source_id?: string;
  nodeset: NodeSetTypes;
  name: string;
  limits?: {
    maximum_number_of_monitored_items: number;
  };
};

export type NodeDataTypes = {
  name: string;
  lastActualValueTimestamp: string;
  lastActualValue: string;
  nodeClass: number;
  smallestSampleIntervall: number;
  nodeId: string;
  parentNodeId: string;
  key: string;
  dataAcquisitionType: string;
  dataType: string | number;
  dataTypeAsString: string;
  unit: {
    code: number;
    display_name: string;
    description: string;
  };
};

export type AlarmNodeDataTypes = {
  nodeId: string;
  name: string;
  unit: string;
  dataType: string;
};

type NodeSelectionStateTypes = "none" | "show" | "edit";

type NodeSelectionTypes = {
  state: NodeSelectionStateTypes;
  nodeData: NodeDataTypes;
  edited: boolean;
};

type AlarmNodeSelectionStateTypes = "none" | "show";

type AlarmNodeSelectionTypes = {
  state: AlarmNodeSelectionStateTypes;
  nodeData: AlarmNodeDataTypes;
};

export type SnackbarNotificationTypes = {
  type: "success" | "error" | "reminder" | "warning";
  text: string;
  state: "open" | "close";
  timeOut?: number;
};

interface RemoveMqttCheckModalTypes {
  showRemoveMqttCheckModal: boolean;
}

export interface ClientIdsTypes {
  namespace: string;
  id: string;
  last_seen: string;
  name: string;
  nodeset?: TreeProps | null;
  created_at: string;
  connected_source_id: string | null;
  connected_source_type: string | null;
}

interface BlueprintInfoTypes {
  name: string;
  description: string;
  author: string;
}
export interface LicensedConfigurationTypes {
  /**
   * If the configuration is loading at the moment
   */
  isLoading: boolean;
  /**
   * If the configuration was loaded at least once
   */
  isReady: boolean;
  /**
   * error if it occurred
   */
  error: any;
  /**
   * If there was an error during loading
   */
  isError: boolean;
  /**
   * Licesed configuration data
   */
  data: LicensedConfigurationResponse;
}

interface CustomerInformationForSupportTicket {
  givenName: string;
  familyName: string;
  email: string;
  namespace: string;
}

export type NodeManagerActiveViewType =
  | "treeStructure"
  | "conditionMonitoring"
  | "dashboard"
  | "shotDataTable"
  | "oneShot"
  | "machineSettings";

interface GlobalDashboardsState {
  value: boolean;
  updatedAt: number;
}

export interface MachineInformationForRequestRemoteAccessDrawer {
  id: string;
  machineId: string;
  gatewayId: string;
  sourceName: string;
  companyName: string;
  dsn: string;
  namespace?: string;
  timePeriod?: string;
  timeDuration?: string;
  status?: MachinesListSupportSessionsStatusTypes;
  requested_duration?: string;
  visibility?: SessionVisibilityType;
  display_name?: string;
  requestMessage?: string;
  started_at?: string;
  userType?: MachinesListSupportSessionsZitadelRoleTypes;
}

export interface TicketModalInformationForRequestTechnicalSupportModal {
  machine_id?: string;
  source_name: string;
}

export interface TicketModalInformationForRequestTechnicalSupportModalWithoutMachine {
  machine_id?: string;
}

export interface SelectedSourceForThingsSupportDrawerTypes {
  sourceId: string;
  sourceName: string;
}

export type Language =
  | "en"
  | "de"
  | "zh"
  | "it"
  | "es"
  | "pt"
  | "nl"
  | "tr"
  | null;

export type Timezone = string | null;

export type OTAActiveViewType = "firmware" | "configurations" | "settings";

export enum FirmwareUploadState {
  FIRMWARE_UPLOAD_STARTED = "firmware-upload-started",
  FIRMWARE_UPLOAD_COMPLETED = "firmware-upload-completed",
  FIRMWARE_UPLOAD_FAILED = "firmware-upload-failed",
}

export enum FirmwareInstallState {
  FIRMWARE_INSTALL_STARTED = "firmware-install-started",
  FIRMWARE_INSTALL_COMPLETED = "firmware-install-completed",
  FIRMWARE_INSTALL_FAILED = "firmware-install-failed",
}

export enum ConfigurationUploadState {
  CONFIGURATION_UPLOAD_STARTED = "configuration-upload-started",
  CONFIGURATION_UPLOAD_COMPLETED = "configuration-upload-completed",
  CONFIGURATION_UPLOAD_FAILED = "configuration-upload-failed",
}

export enum ConfigurationInstallState {
  CONFIGURATION_INSTALL_STARTED = "configuration-install-started",
  CONFIGURATION_INSTALL_COMPLETED = "configuration-install-completed",
  CONFIGURATION_INSTALL_FAILED = "configuration-install-failed",
}

export interface YourState {
  // Define your state properties and types here
  activeMachineStructureNode: string;
  activeInitialStructureNode: string;
  addMachineGroupsAdminPanelModal: AddMachineGroupsAdminPanelModalTypes;
  authContext: AuthContext | null;
  clientConfiguration: ClientConfigurationTypes;
  mqttConfiguration: MqttConfigurationTypes;
  clientIdsArray: ClientIdsTypes[];
  clientRegistrationView: boolean;
  connectedServerListData: SourceTypes[];
  connectionView: ServerConnectionViewTypes;
  customerInformationForSupportTicket: CustomerInformationForSupportTicket;
  deleteIconActive: boolean;
  disconnectedServerListData: SourceTypes[];
  allMachinesListData: Record<
    string,
    transformedGatewaysWithMachinesListEntryTypes
  >;
  supportTicketInformation: supportTicketByIdType;
  supportCommentsByTicket: GetSupportCommentsByTicketResponse;
  streamingAiResponseText: string;
  streamingAiResponseTools: StreamTool[];
  streamingTicketAttachments: TicketCommentAttachment[];
  isStreamingAiResponse: boolean;
  streamingTicketId: string | null;
  searchMachinesListData: Record<
    string,
    transformedGatewaysWithMachinesListEntryTypes
  >;
  machinesManagerSearchTerms: string[];
  machinesManagerSearchQuery: string;
  machinesManagerShowSearchBar: boolean;
  userManagementSearchTerms: string[];
  userManagementSearchQuery: string;
  allGatewaysWithMachinesListData: Record<
    string,
    GatewayWithSourceViewStateTypes
  >;
  editStructurePageInitialData: TreeProps;
  editStructurePageInitialDataFiltered: TreeProps;
  editStructurePageTransferData: TreeProps;
  editStructurePageTransferDataFiltered: TreeProps;
  findBlueprintModal: FindBlueprintModalTypes;
  connectedGatewaysListData: GatewayWithSourceViewStateTypes[];
  disconnectedGatewaysListData: GatewayWithSourceViewStateTypes[];
  initialFilterModal: FilterModalTypes;
  isBlueprintApplied: boolean;
  isOneClickSolutionActive: boolean;
  language: Language;
  timezone: Timezone;
  licensedConfigurations: LicensedConfigurationTypes;
  machineStructureFilterModal: FilterModalTypes;
  maximum_number_of_monitored_items: number;
  mqttModal: MqttModalTypes;
  namespace: string;
  nodeSelection: NodeSelectionTypes;
  alarmNodeSelection: AlarmNodeSelectionTypes;
  nodeManagerActiveView: NodeManagerActiveViewType;
  userManagementListActiveView: UserManagementListActiveViewType;
  removeMqttCheckModal: RemoveMqttCheckModalTypes;
  saveBlueprintModal: SaveBlueprintModalTypes;
  autoMatchBlueprintModal: AutoMatchBlueprintModalTypes;
  myBlueprintLibraryModal: MyBlueprintLibraryModalTypes;
  scanIdForInProgressScan: string | undefined;
  scanInterruptionModalState: boolean;
  scanModal: ScanModalTypes;
  selectedAndDraggedData: TreeProps | undefined;
  serverListsResultsReady: boolean;
  gatewaysWithSourcesResultsReady: boolean;
  userListResultsReady: string;
  showBulkEditDataAcquisitionTypeIcon: boolean;
  showBulkEditDataAcquisitionTypeModal: boolean;
  showDropAreaDeactivationOverlay: boolean;
  showMachineStructureDropAreaDeactivationOverlay: boolean;
  showOverwriteStructureWarningModal: boolean;
  showRenderOnlyForDevelopersStyle: boolean;
  showRenderUnreleasedFeaturesStyle: boolean;
  snackbarNotification: SnackbarNotificationTypes;
  structureWasSaved: boolean;
  unassignedBlueprintNodeDataFiltered: TreeProps;
  unsavedStructureModal: UnsavedStructureModalTypes;
  unsavedStructureForNodeManagerNavigationModal: UnsavedStructureForNodeManagerNavigationModalTypes;
  unsavedConditionMonitoringForNodeManagerNavigationModal: UnsavedConditionMonitoringForNodeManagerNavigationModalTypes;
  updatedBlueprintInfo: BlueprintInfoTypes;
  warnBeforeScanModalState: boolean;
  modals: {
    tileDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    alarmProfilesDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    alarmProfileEmailRecipientsDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    sourceInfoDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    addNewGatewayDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    addNewMachineDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    devDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    addUserDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    /* addGatewayServiceUserDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    }; */
    addServiceUserDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    editUserDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    editServiceUserDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    machinesRequestRemoteMachineAccessDrawerModal: {
      machineInformation: MachineInformationForRequestRemoteAccessDrawer;
      placement: string;
      showModal: boolean;
      size: string;
    };
    addRequestTechnicalSupportDrawerModal: {
      ticketModalInformation: TicketModalInformationForRequestTechnicalSupportModal;
      placement: string;
      showModal: boolean;
      size: string;
    };
    otaFirmwareVersionDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    otaConfigurationDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    otaFirmwareBackupDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    otaConfigurationBackupDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    addRequestTechnicalSupportDrawerModalWithoutMachine: {
      ticketModalInformation: TicketModalInformationForRequestTechnicalSupportModalWithoutMachine;
      placement: string;
      showModal: boolean;
      size: string;
    };
    editWidgetDrawerModal: {
      tileId: string | null;
      placement: string;
      showModal: boolean;
      size: string;
    };
    widgetPickerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    machinesCancelRequestDisconnectSupportModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
    supportChatDrawerModal: {
      showModal: boolean;
    };
    alarmsNotificationsDrawerModal: {
      placement: string;
      showModal: boolean;
      size: string;
    };
  };
  globalLookForLastTimeStampBeforeFetchingData: GlobalDashboardsState;
  duplicateStructureIds: string[];
  currentlySelectedSourceForThingsSupportDrawer: SelectedSourceForThingsSupportDrawerTypes;
  selectedUserInEditUserDrawer: ZitadelUser | null;
  selectedServiceUserInEditUserDrawer: ZitadelServiceUser | null;
  isAIRagChatOpen: boolean;
  rolesDescriptions: RolesDescriptions;
  otaActiveView: OTAActiveViewType;
  firmwareUploadModal: FirmwareUploadModalTypes;
  firmwareInstallModal: FirmwareInstallModalTypes;
  configurationUploadModal: ConfigurationUploadModalTypes;
  configurationInstallModal: ConfigurationInstallModalTypes;
  otaDownloadModal: OtaDownloadModalTypes;
  configurationBackupInstallModal: ConfigurationBackupInstallModalTypes;
  firmwareBackupInstallModal: FirmwareBackupInstallModalTypes;
  otaPushFirmwareModal: OtaPushFirmwareModalTypes;
  otaCreateBackupModal: OtaCreateBackupModalTypes;
  otaConfirmFirmwareInstallationModal: OtaConfirmFirmwareInstallationModalTypes;
  otaConfirmBackupProcessModal: OtaConfirmBackupProcessModalTypes;
  otaConfirmFirmwareBackupInstallationModal: OtaConfirmFirmwareBackupInstallationModalTypes;
  otaPushFirmwareBackupModal: OtaPushFirmwareBackupModalTypes;
  otaAcceptAndPushFirmwareModal: OtaAcceptAndPushFirmwareModalTypes;
  otaAcceptAndCreateBackupProcessModal: OtaAcceptAndCreateBackupProcessModalTypes;
  otaInstallationInProgressModal: OtaInstallationInProgressModalTypes;
  otaInstallationCompleteModal: OtaInstallationCompleteModalTypes;
  otaInstallationFailedModal: OtaInstallationFailedModalTypes;
}

export const initialSnackbarNotification: SnackbarNotificationTypes = {
  type: "reminder",
  text: "",
  state: "close",
  timeOut: 2500,
};

export const defaultConnectionView: ServerConnectionViewTypes = {
  source: {
    connected_via_client: "",
    connection_state: "",
    created_at: "",
    dsn: "",
    hostname: "",
    last_active_at: null,
    last_updated_at: "",
    limits: { maximum_number_of_monitored_items: 0 },
    name: "",
    namespace: "",
    nodeset: null,
    port: 0,
    source_id: "",
    source_type: "",
    build_info: {
      product_name: "",
      manufacturer: "",
      software_version: null,
    },
    auth_info: {
      security_mode: DeviceSecurityMode.NONE,
      security_policy: DeviceSecurityPolicy.NONE,
      username: "",
      password: "",
      client_certificate_private_key_base64: "",
      client_certificate_password: "",
      client_certificate_base64: "",
      server_public_key_base64: "",
    },
    source_key: "",
  },
  state: "none",
  authentication: DeviceAuthOption.ANONYMOUS,
};

export const emptyNodeDataObject = {
  name: "",
  lastActualValueTimestamp: "",
  lastActualValue: "",
  nodeClass: 0,
  smallestSampleIntervall: 0,
  nodeId: "",
  parentNodeId: "",
  key: "",
  dataAcquisitionType: "",
  dataType: "",
  dataTypeAsString: "",
  unit: null,
};

export const emptyAlarmNodeData = {
  nodeId: "",
  name: "",
  unit: null,
  dataType: "",
};

export const initialState: YourState = {
  addMachineGroupsAdminPanelModal: {
    showAddMachineGroupsAdminPanelModal: false,
  },
  clientRegistrationView: false,
  showRenderOnlyForDevelopersStyle: true, // DO NOT SET THIS TO FALSE!
  showRenderUnreleasedFeaturesStyle: true, // DO NOT SET THIS TO FALSE!
  scanModal: {
    showScanModal: false,
    scanModalState: ScanStatesConsts.SCAN_STARTED,
  },
  warnBeforeScanModalState: false,
  scanIdForInProgressScan: undefined,
  scanInterruptionModalState: false,
  serverListsResultsReady: false,
  gatewaysWithSourcesResultsReady: false,
  userListResultsReady: "",
  connectedServerListData: [],
  disconnectedServerListData: [],
  allMachinesListData: {},
  searchMachinesListData: {},
  supportTicketInformation: {} as supportTicketByIdType,
  supportCommentsByTicket: {} as GetSupportCommentsByTicketResponse,
  streamingAiResponseText: "",
  streamingAiResponseTools: [],
  streamingTicketAttachments: [],
  isStreamingAiResponse: false,
  streamingTicketId: null,
  machinesManagerSearchTerms: [],
  machinesManagerSearchQuery: "",
  machinesManagerShowSearchBar: true,
  userManagementSearchTerms: [],
  userManagementSearchQuery: "",
  allGatewaysWithMachinesListData: {},
  connectedGatewaysListData: [],
  disconnectedGatewaysListData: [],
  connectionView: defaultConnectionView,

  autoMatchBlueprintModal: {
    showAutoMatchBlueprintModal: false,
    autoMatchBlueprintModalContentShown: "find",
    selectedBlueprint: undefined,
  },

  myBlueprintLibraryModal: {
    showMyBlueprintLibraryModal: false,
    myBlueprintLibraryModalContentShown: "find",
    selectedBlueprint: undefined,
  },

  saveBlueprintModal: {
    showSaveBlueprintModal: false,
    blueprintModalContentShown: "save",
    selectedBlueprint: undefined,
  },

  findBlueprintModal: {
    showFindBlueprintModal: false,
    blueprintModalContentShown: "find",
    selectedFindBlueprint: undefined,
  },

  unsavedStructureModal: {
    showUnsavedStructureModal: false,
    routeToNavigateAfterConfirmation: undefined,
  },
  unsavedStructureForNodeManagerNavigationModal: {
    showUnsavedStructureForNodeManagerNavigationModal: false,
    activeViewForNavigation: "treeStructure",
  },
  unsavedConditionMonitoringForNodeManagerNavigationModal: {
    showUnsavedConditionMonitoringForNodeManagerNavigationModal: false,
    activeViewForNavigation: "treeStructure",
  },

  showOverwriteStructureWarningModal: false,
  editStructurePageInitialData: undefined,
  editStructurePageInitialDataFiltered: undefined,
  editStructurePageTransferData: undefined,
  editStructurePageTransferDataFiltered: undefined,
  unassignedBlueprintNodeDataFiltered: undefined,

  selectedAndDraggedData: undefined,

  structureWasSaved: true,

  initialFilterModal: {
    showFilterModal: false,
    filterInputContent: "",
  },
  language: null,
  timezone: null,
  maximum_number_of_monitored_items: 1,
  machineStructureFilterModal: {
    showFilterModal: false,
    filterInputContent: "",
  },
  mqttModal: {
    showMqttModal: false,
    mqttModalState: "mqtt-add-connection",
    errors: [],
  },
  clientConfiguration: emptyClientConfiguration,
  mqttConfiguration: emptyMqttConfig,
  removeMqttCheckModal: {
    showRemoveMqttCheckModal: false,
  },
  nodeSelection: {
    state: "none",
    nodeData: emptyNodeDataObject,
    edited: false,
  },
  alarmNodeSelection: {
    state: "none",
    nodeData: emptyAlarmNodeData,
  },
  nodeManagerActiveView: "treeStructure",
  userManagementListActiveView: "users",
  snackbarNotification: initialSnackbarNotification,
  activeMachineStructureNode: "",
  activeInitialStructureNode: "",
  deleteIconActive: false,
  clientIdsArray: [],
  namespace: "",
  authContext: null,
  showDropAreaDeactivationOverlay: false,
  showMachineStructureDropAreaDeactivationOverlay: false,
  showBulkEditDataAcquisitionTypeModal: false,
  showBulkEditDataAcquisitionTypeIcon: false,
  isOneClickSolutionActive: false,
  isBlueprintApplied: false,
  updatedBlueprintInfo: {
    name: "",
    description: "",
    author: "",
  },
  licensedConfigurations: {
    isLoading: false,
    isReady: false,
    error: null,
    isError: false,
    data: undefined,
  },
  customerInformationForSupportTicket: {
    givenName: "",
    familyName: "",
    email: "",
    namespace: "",
  },
  modals: {
    tileDrawerModal: {
      placement: "right",
      showModal: false,
      size: "50%",
    },
    machinesCancelRequestDisconnectSupportModal: {
      placement: "right",
      showModal: false,
      size: "50%",
    },
    alarmProfilesDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    alarmProfileEmailRecipientsDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    sourceInfoDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    addNewGatewayDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    addNewMachineDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    devDrawerModal: {
      placement: "left",
      showModal: false,
      size: "348px" /* "600px" */,
    },
    addUserDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    /* addGatewayServiceUserDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    }, */
    addServiceUserDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    editUserDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    editServiceUserDrawerModal: {
      placement: "right",
      showModal: false,
      size: "360px",
    },
    machinesRequestRemoteMachineAccessDrawerModal: {
      machineInformation: {
        id: "",
        machineId: "",
        gatewayId: "",
        sourceName: "",
        companyName: "",
        dsn: "",
        requestMessage: "",
        userType: undefined,
      },
      placement: "right",
      showModal: false,
      size: "348px",
    },
    addRequestTechnicalSupportDrawerModal: {
      ticketModalInformation: {
        machine_id: undefined,
        source_name: "",
      },
      placement: "right",
      showModal: false,
      size: "348px",
    },
    addRequestTechnicalSupportDrawerModalWithoutMachine: {
      ticketModalInformation: {
        machine_id: undefined,
      },
      placement: "right",
      showModal: false,
      size: "348px",
    },
    editWidgetDrawerModal: {
      tileId: null,
      placement: "right",
      showModal: false,
      size: "450px",
    },
    widgetPickerModal: {
      placement: "center",
      showModal: false,
      size: "50%",
    },
    otaFirmwareVersionDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    supportChatDrawerModal: {
      showModal: false,
    },
    otaConfigurationDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    otaFirmwareBackupDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    otaConfigurationBackupDrawerModal: {
      placement: "right",
      showModal: false,
      size: "348px",
    },
    alarmsNotificationsDrawerModal: {
      placement: "right",
      showModal: false,
      size: "500px",
    },
  },
  globalLookForLastTimeStampBeforeFetchingData: {
    value: false,
    updatedAt: Date.now(),
  },
  duplicateStructureIds: [],
  currentlySelectedSourceForThingsSupportDrawer: {
    sourceId: "",
    sourceName: "",
  },
  selectedUserInEditUserDrawer: null,
  selectedServiceUserInEditUserDrawer: null,
  isAIRagChatOpen: false,
  rolesDescriptions: {},
  otaActiveView: "firmware",
  firmwareUploadModal: {
    showFirmwareUploadModal: false,
    firmwareUploadModalState: FirmwareUploadState.FIRMWARE_UPLOAD_STARTED,
    firmwareUploadModalResponseMessage: "",
  },
  firmwareInstallModal: {
    showFirmwareInstallModal: false,
  },
  configurationUploadModal: {
    showConfigurationUploadModal: false,
    configurationUploadModalState:
      ConfigurationUploadState.CONFIGURATION_UPLOAD_STARTED,
    configurationUploadModalResponseMessage: "",
  },
  configurationInstallModal: {
    showConfigurationInstallModal: false,
  },
  otaDownloadModal: {
    showOtaDownloadModal: false,
  },
  configurationBackupInstallModal: {
    showConfigurationBackupInstallModal: false,
  },
  firmwareBackupInstallModal: {
    showFirmwareBackupInstallModal: false,
  },
  otaPushFirmwareModal: {
    showOtaPushFirmwareModal: false,
  },
  otaCreateBackupModal: {
    showOtaCreateBackupModal: false,
  },
  otaConfirmFirmwareInstallationModal: {
    showOtaConfirmFirmwareInstallationModal: false,
  },
  otaConfirmBackupProcessModal: {
    showOtaConfirmBackupProcessModal: false,
  },
  otaConfirmFirmwareBackupInstallationModal: {
    showOtaConfirmFirmwareBackupInstallationModal: false,
  },
  otaPushFirmwareBackupModal: {
    showOtaPushFirmwareBackupModal: false,
  },
  otaAcceptAndPushFirmwareModal: {
    showOtaAcceptAndPushFirmwareModal: false,
  },
  otaAcceptAndCreateBackupProcessModal: {
    showOtaAcceptAndCreateBackupProcessModal: false,
  },
  otaInstallationInProgressModal: {
    showOtaInstallationInProgressModal: false,
  },
  otaInstallationCompleteModal: {
    showOtaInstallationCompleteModal: false,
  },
  otaInstallationFailedModal: {
    showOtaInstallationFailedModal: false,
  },
};

const yourSlice = createSlice({
  name: "yourSlice",
  initialState,
  reducers: {
    updateShowRenderOnlyForDevelopersStyle: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showRenderOnlyForDevelopersStyle = action.payload;
    },
    updateShowRenderUnreleasedFeaturesStyle: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showRenderUnreleasedFeaturesStyle = action.payload;
    },
    updateShowAddMachineGroupsAdminPanelModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.addMachineGroupsAdminPanelModal.showAddMachineGroupsAdminPanelModal =
        action.payload;
    },
    updateScanIdForInProgressScan: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.scanIdForInProgressScan = action.payload;
    },
    updateShowScanModal: (state, action: PayloadAction<boolean>) => {
      state.scanModal.showScanModal = action.payload;
    },
    updateScanModalState: (state, action: PayloadAction<string>) => {
      state.scanModal.scanModalState = action.payload;
    },
    updateWarnScanModalState: (state, action: PayloadAction<boolean>) => {
      state.warnBeforeScanModalState = action.payload;
    },
    updateScanInterruptionModalState: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.scanInterruptionModalState = action.payload;
    },
    updateServerListsResultsReady: (state, action: PayloadAction<boolean>) => {
      state.serverListsResultsReady = action.payload;
    },
    updateGatewaysWithSourcesResultsReady: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.gatewaysWithSourcesResultsReady = action.payload;
    },
    updateUserListResultsReady: (state, action: PayloadAction<string>) => {
      state.userListResultsReady = action.payload;
    },
    updateConnectedServerListData: (
      state,
      action: PayloadAction<SourceTypes[]>,
    ) => {
      state.connectedServerListData = action.payload;
    },
    updateDisconnectedServerListData: (
      state,
      action: PayloadAction<SourceTypes[]>,
    ) => {
      state.disconnectedServerListData = action.payload;
    },
    updateAllMachinesListData: (
      state,
      action: PayloadAction<
        Record<string, transformedGatewaysWithMachinesListEntryTypes>
      >,
    ) => {
      state.allMachinesListData = action.payload;
    },
    updateSupportTicketInformation: (
      state,
      action: PayloadAction<supportTicketByIdType>,
    ) => {
      state.supportTicketInformation = action.payload;
    },
    updateSupportCommentsByTicket: (
      state,
      action: PayloadAction<GetSupportCommentsByTicketResponse>,
    ) => {
      state.supportCommentsByTicket = action.payload;
    },
    updateStreamingAiResponseText: (state, action: PayloadAction<string>) => {
      state.streamingAiResponseText = action.payload;
    },
    updateStreamingAiResponseTools: (
      state,
      action: PayloadAction<StreamTool[]>,
    ) => {
      state.streamingAiResponseTools = action.payload;
    },
    updateIsStreamingAiResponse: (state, action: PayloadAction<boolean>) => {
      state.isStreamingAiResponse = action.payload;
    },
    updateStreamingTicketId: (state, action: PayloadAction<string | null>) => {
      state.streamingTicketId = action.payload;
    },
    updateStreamingTicketAttachments: (
      state,
      action: PayloadAction<TicketCommentAttachment[]>,
    ) => {
      state.streamingTicketAttachments = action.payload;
    },
    resetStreamingAiResponse: (state) => {
      state.streamingAiResponseText = "";
      state.streamingAiResponseTools = [];
      state.streamingTicketAttachments = [];
      state.isStreamingAiResponse = false;
      state.streamingTicketId = null;
    },
    updateSearchMachinesListData: (
      state,
      action: PayloadAction<
        Record<string, transformedGatewaysWithMachinesListEntryTypes>
      >,
    ) => {
      state.searchMachinesListData = action.payload;
    },
    updateMachinesManagerSearchTerms: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.machinesManagerSearchTerms = action.payload;
    },
    updateMachinesManagerSearchQuery: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.machinesManagerSearchQuery = action.payload;
    },
    updateMachinesManagerShowSearchBar: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.machinesManagerShowSearchBar = action.payload;
    },
    updateUserManagementSearchTerms: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      state.userManagementSearchTerms = action.payload;
    },
    updateUserManagementSearchQuery: (state, action: PayloadAction<string>) => {
      state.userManagementSearchQuery = action.payload;
    },
    updateConnectionView: (
      state,
      action: PayloadAction<ServerConnectionViewTypes>,
    ) => {
      state.connectionView = action.payload;
    },
    updateShowSaveBlueprintModal: (state, action: PayloadAction<boolean>) => {
      state.saveBlueprintModal.showSaveBlueprintModal = action.payload;
    },
    updateBlueprintModalContentShown: (
      state,
      action: PayloadAction<"save" | "replace">,
    ) => {
      state.saveBlueprintModal.blueprintModalContentShown = action.payload;
    },
    updateSelectedBlueprint: (
      state,
      action: PayloadAction<BlueprintsArrayObjectTypes>,
    ) => {
      state.saveBlueprintModal.selectedBlueprint = action.payload;
    },
    updateShowAutoMatchBlueprintModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.autoMatchBlueprintModal.showAutoMatchBlueprintModal =
        action.payload;
    },
    updateAutoMatchBlueprintModalContentShown: (
      state,
      action: PayloadAction<"find" | "apply">,
    ) => {
      state.autoMatchBlueprintModal.autoMatchBlueprintModalContentShown =
        action.payload;
    },
    updateSelectedAutoMatchBlueprint: (
      state,
      action: PayloadAction<BlueprintsArrayObjectTypes>,
    ) => {
      state.autoMatchBlueprintModal.selectedBlueprint = action.payload;
    },
    updateShowMyBlueprintLibraryModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.myBlueprintLibraryModal.showMyBlueprintLibraryModal =
        action.payload;
    },
    updateMyBlueprintLibraryModalContentShown: (
      state,
      action: PayloadAction<"find" | "apply">,
    ) => {
      state.myBlueprintLibraryModal.myBlueprintLibraryModalContentShown =
        action.payload;
    },
    updateSelectedMyBlueprintLibraryBlueprint: (
      state,
      action: PayloadAction<BlueprintsArrayObjectTypes>,
    ) => {
      state.myBlueprintLibraryModal.selectedBlueprint = action.payload;
    },

    updateShowFindBlueprintModal: (state, action: PayloadAction<boolean>) => {
      state.findBlueprintModal.showFindBlueprintModal = action.payload;
    },
    updateFindBlueprintModalContentShown: (
      state,
      action: PayloadAction<"find" | "apply">,
    ) => {
      state.findBlueprintModal.blueprintModalContentShown = action.payload;
    },
    updateSelectedFindBlueprint: (
      state,
      action: PayloadAction<BlueprintsArrayObjectTypes>,
    ) => {
      state.findBlueprintModal.selectedFindBlueprint = action.payload;
    },

    updateShowUnsavedStructureModal: (
      state,
      action: PayloadAction<UnsavedStructureModalTypes>,
    ) => {
      state.unsavedStructureModal = action.payload;
    },
    updateShowUnsavedStructureForNodeManagerNavigationModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.unsavedStructureForNodeManagerNavigationModal.showUnsavedStructureForNodeManagerNavigationModal =
        action.payload;
    },
    updateActiveViewUnsavedStructureForNodeManagerNavigationModal: (
      state,
      action: PayloadAction<NodeManagerActiveViewType>,
    ) => {
      state.unsavedStructureForNodeManagerNavigationModal.activeViewForNavigation =
        action.payload;
    },
    updateEditStructurePageInitialData: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.editStructurePageInitialData = action.payload;
    },
    updateEditStructurePageInitialDataFiltered: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.editStructurePageInitialDataFiltered = action.payload;
    },
    // DO NOT use this function directly, use saveEditStructurePageTransferData instead!
    updateEditStructurePageTransferData: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.editStructurePageTransferData = action.payload;
    },
    updateEditStructurePageTransferDataFiltered: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.editStructurePageTransferDataFiltered = action.payload;
    },
    updateUnassignedBlueprintNodeDataFiltered: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.unassignedBlueprintNodeDataFiltered = action.payload;
    },
    updateSelectedAndDraggedNodeData: (
      state,
      action: PayloadAction<TreeProps>,
    ) => {
      state.selectedAndDraggedData = action.payload;
    },

    updateStructureWasSaved: (state, action: PayloadAction<boolean>) => {
      state.structureWasSaved = action.payload;
    },
    updateShowInitialFilterModal: (state, action: PayloadAction<boolean>) => {
      state.initialFilterModal.showFilterModal = action.payload;
    },
    updateInitialFilterModalFilterInputContent: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.initialFilterModal.filterInputContent = action.payload;
    },
    updateShowMachineStructureFilterModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.machineStructureFilterModal.showFilterModal = action.payload;
    },
    updateShowOverwriteStructureWarningModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showOverwriteStructureWarningModal = action.payload;
    },
    updateMaximumNumberOfMonitoredItems: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.maximum_number_of_monitored_items = action.payload;
    },
    updateMachineStructureFilterModalFilterInputContent: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.machineStructureFilterModal.filterInputContent = action.payload;
    },
    updateShowMqttModal: (state, action: PayloadAction<boolean>) => {
      state.mqttModal.showMqttModal = action.payload;
    },
    updateClientConfiguration: (
      state,
      action: PayloadAction<ClientConfigurationTypes>,
    ) => {
      state.clientConfiguration = action.payload;
    },
    updateMqttConfiguration: (
      state,
      action: PayloadAction<MqttConfigurationTypes>,
    ) => {
      state.mqttConfiguration = action.payload;
    },
    updateStateMqttModal: (
      state,
      action: PayloadAction<MqttModalStateTypes>,
    ) => {
      state.mqttModal.mqttModalState = action.payload;
    },
    updateErrorsMqttModal: (
      state,
      action: PayloadAction<MqttConnectionErrorTypes[]>,
    ) => {
      state.mqttModal.errors = action.payload;
    },
    updateShowRemoveMqttCheckModal: (state, action: PayloadAction<boolean>) => {
      state.removeMqttCheckModal.showRemoveMqttCheckModal = action.payload;
    },
    updateNodeSelectionState: (
      state,
      action: PayloadAction<NodeSelectionStateTypes>,
    ) => {
      state.nodeSelection.state = action.payload;
    },
    updateAlarmNodeSelectionData: (
      state,
      action: PayloadAction<AlarmNodeDataTypes>,
    ) => {
      state.alarmNodeSelection.nodeData = action.payload;
    },
    updateAlarmNodeSelectionState: (
      state,
      action: PayloadAction<AlarmNodeSelectionStateTypes>,
    ) => {
      state.alarmNodeSelection.state = action.payload;
    },
    updateNodeSelectionNodeData: (
      state,
      action: PayloadAction<NodeDataTypes>,
    ) => {
      state.nodeSelection.nodeData = action.payload;
    },
    updateNodeSelectionNodeEditedState: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.nodeSelection.edited = action.payload;
    },
    updateNodeManagerActiveView: (
      state,
      action: PayloadAction<NodeManagerActiveViewType>,
    ) => {
      state.nodeManagerActiveView = action.payload;
    },
    updateUserManagementListActiveView: (
      state,
      action: PayloadAction<UserManagementListActiveViewType>,
    ) => {
      if (state.userManagementListActiveView !== action.payload) {
        state.userManagementSearchTerms = [];
        state.userManagementSearchQuery = "";
      }
      state.userManagementListActiveView = action.payload;
    },
    updateSnackbarNotification: (
      state,
      action: PayloadAction<SnackbarNotificationTypes>,
    ) => {
      state.snackbarNotification = action.payload;
    },
    updateActiveMachineStructureNode: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.activeMachineStructureNode = action.payload;
    },
    updateActiveInitialStructureNode: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.activeInitialStructureNode = action.payload;
    },
    updateClientIds: (state, action: PayloadAction<ClientIdsTypes[]>) => {
      state.clientIdsArray = action.payload;
    },
    updateNamespace: (state, action: PayloadAction<string>) => {
      state.namespace = action.payload;
    },
    updateDeleteIconActiveState: (state, action: PayloadAction<boolean>) => {
      state.deleteIconActive = action.payload;
    },
    updateAuthContext: (state, action: PayloadAction<AuthContext | null>) => {
      state.authContext = action.payload;
    },
    updateShowDropAreaDeactivationOverlay: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showDropAreaDeactivationOverlay = action.payload;
    },
    updateShowMachineStructureDropAreaDeactivationOverlay: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showMachineStructureDropAreaDeactivationOverlay = action.payload;
    },
    updateShowBulkEditDataAcquisitionTypeModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showBulkEditDataAcquisitionTypeModal = action.payload;
    },

    updateShowUnsavedConditionMonitoringForNodeManagerNavigationModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.unsavedConditionMonitoringForNodeManagerNavigationModal.showUnsavedConditionMonitoringForNodeManagerNavigationModal =
        action.payload;
    },
    updateActiveViewUnsavedConditionMonitoringForNodeManagerNavigationModal: (
      state,
      action: PayloadAction<NodeManagerActiveViewType>,
    ) => {
      state.unsavedConditionMonitoringForNodeManagerNavigationModal.activeViewForNavigation =
        action.payload;
    },

    updateShowBulkEditDataAcquisitionTypeIcon: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showBulkEditDataAcquisitionTypeIcon = action.payload;
    },
    updateIsOneClickSolutionActive: (state, action: PayloadAction<boolean>) => {
      state.isOneClickSolutionActive = action.payload;
    },
    updateIsBlueprintApplied: (state, action: PayloadAction<boolean>) => {
      state.isBlueprintApplied = action.payload;
    },
    updateUpdatedBlueprintInfo: (
      state,
      action: PayloadAction<BlueprintInfoTypes>,
    ) => {
      state.updatedBlueprintInfo = action.payload;
    },
    updateLicensedConfigurationsLoading: (state) => {
      state.licensedConfigurations.isLoading = true;
      state.licensedConfigurations.error = null;
      state.licensedConfigurations.isError = false;
    },
    updateLicensedConfigurationsLoaded: (
      state,
      action: PayloadAction<LicensedConfigurationResponse>,
    ) => {
      state.licensedConfigurations.data = action.payload;
      state.licensedConfigurations.isReady = true;
      state.licensedConfigurations.isLoading = false;
      state.licensedConfigurations.error = null;
      state.licensedConfigurations.isError = false;
    },
    updateLicensedConfigurationsError: (state, action: PayloadAction<any>) => {
      state.licensedConfigurations.error = action.payload;
      state.licensedConfigurations.isError = true;
      state.licensedConfigurations.isLoading = false;
    },
    updateCustomerInformationForSupportTicket: (
      state,
      action: PayloadAction<CustomerInformationForSupportTicket>,
    ) => {
      state.customerInformationForSupportTicket = action.payload;
    },
    updateModalsCloseDrawerModal: (
      state,
      action: PayloadAction<{ showModal: boolean; id: string }>,
    ) => {
      state.modals[action.payload.id].showModal = action.payload.showModal;
    },
    updateMachineInformationForRequestRemoteAccessDrawerModal: (
      state,
      action: PayloadAction<MachineInformationForRequestRemoteAccessDrawer>,
    ) => {
      state.modals.machinesRequestRemoteMachineAccessDrawerModal.machineInformation =
        action.payload;
    },
    updateAddRequestTechnicalSupportDrawerModal: (
      state,
      action: PayloadAction<TicketModalInformationForRequestTechnicalSupportModal>,
    ) => {
      state.modals.addRequestTechnicalSupportDrawerModal.ticketModalInformation =
        action.payload;
    },
    updateAddRequestTechnicalSupportDrawerModalWithoutMachine: (
      state,
      action: PayloadAction<TicketModalInformationForRequestTechnicalSupportModalWithoutMachine>,
    ) => {
      state.modals.addRequestTechnicalSupportDrawerModalWithoutMachine.ticketModalInformation =
        action.payload;
    },
    updateEditWidgetDrawerModalTileId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.modals.editWidgetDrawerModal.tileId = action.payload;
    },
    updateGlobalLookForLastTimeStampBeforeFetchingData: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.globalLookForLastTimeStampBeforeFetchingData = {
        value: action.payload,
        updatedAt: Date.now(),
      };
    },
    updateDuplicateStructureIds: (state, action: PayloadAction<string[]>) => {
      state.duplicateStructureIds = action.payload;
    },
    updateConnectedGatewaysListData: (
      state,
      action: PayloadAction<GatewayWithSourceViewStateTypes[]>,
    ) => {
      state.connectedGatewaysListData = action.payload;
    },
    updateDisconnectedGatewaysListData: (
      state,
      action: PayloadAction<GatewayWithSourceViewStateTypes[]>,
    ) => {
      state.disconnectedGatewaysListData = action.payload;
    },
    updateLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
    },
    updateTimezone: (state, action: PayloadAction<Timezone>) => {
      state.timezone = action.payload;
    },
    updateCurrentlySelectedUserForEditUserDrawer: (
      state,
      action: PayloadAction<ZitadelUser | null>,
    ) => {
      state.selectedUserInEditUserDrawer = action.payload;
    },
    updateCurrentlySelectedServiceUserForEditUserDrawer: (
      state,
      action: PayloadAction<ZitadelServiceUser | null>,
    ) => {
      state.selectedServiceUserInEditUserDrawer = action.payload;
    },
    updateIsAIRagChatOpen: (state, action: PayloadAction<boolean>) => {
      state.isAIRagChatOpen = action.payload;
    },
    updateRolesDescriptions: (
      state,
      action: PayloadAction<RolesDescriptions>,
    ) => {
      state.rolesDescriptions = action.payload;
    },
    updateOTAActiveView: (state, action: PayloadAction<OTAActiveViewType>) => {
      state.otaActiveView = action.payload;
    },
    updateShowFirmwareUploadModal: (state, action: PayloadAction<boolean>) => {
      state.firmwareUploadModal.showFirmwareUploadModal = action.payload;
    },
    updateFirmwareUploadModalState: (
      state,
      action: PayloadAction<FirmwareUploadState>,
    ) => {
      state.firmwareUploadModal.firmwareUploadModalState = action.payload;
    },
    updateFirmwareUploadModalMessage: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.firmwareUploadModal.firmwareUploadModalResponseMessage =
        action.payload;
    },
    updateShowFirmwareInstallModal: (state, action: PayloadAction<boolean>) => {
      state.firmwareInstallModal.showFirmwareInstallModal = action.payload;
    },
    updateShowConfigurationUploadModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.configurationUploadModal.showConfigurationUploadModal =
        action.payload;
    },
    updateConfigurationUploadModalState: (
      state,
      action: PayloadAction<ConfigurationUploadState>,
    ) => {
      state.configurationUploadModal.configurationUploadModalState =
        action.payload;
    },
    updateConfigurationUploadModalMessage: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.configurationUploadModal.configurationUploadModalResponseMessage =
        action.payload;
    },
    updateShowConfigurationInstallModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.configurationInstallModal.showConfigurationInstallModal =
        action.payload;
    },
    updateShowOtaDownloadModal: (state, action: PayloadAction<boolean>) => {
      state.otaDownloadModal.showOtaDownloadModal = action.payload;
    },
    updateShowConfigurationBackupInstallModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.configurationBackupInstallModal.showConfigurationBackupInstallModal =
        action.payload;
    },
    updateShowFirmwareBackupInstallModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.firmwareBackupInstallModal.showFirmwareBackupInstallModal =
        action.payload;
    },
    updateShowOtaPushFirmwareModal: (state, action: PayloadAction<boolean>) => {
      state.otaPushFirmwareModal.showOtaPushFirmwareModal = action.payload;
    },
    updateShowOtaCreateBackupModal: (state, action: PayloadAction<boolean>) => {
      state.otaCreateBackupModal.showOtaCreateBackupModal = action.payload;
    },
    updateShowOtaConfirmFirmwareInstallationModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaConfirmFirmwareInstallationModal.showOtaConfirmFirmwareInstallationModal =
        action.payload;
    },
    updateShowOtaConfirmBackupProcessModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaConfirmBackupProcessModal.showOtaConfirmBackupProcessModal =
        action.payload;
    },
    updateShowOtaConfirmFirmwareBackupInstallationModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaConfirmFirmwareBackupInstallationModal.showOtaConfirmFirmwareBackupInstallationModal =
        action.payload;
    },
    updateShowOtaPushFirmwareBackupModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaPushFirmwareBackupModal.showOtaPushFirmwareBackupModal =
        action.payload;
    },
    updateShowOtaAcceptAndPushFirmwareModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaAcceptAndPushFirmwareModal.showOtaAcceptAndPushFirmwareModal =
        action.payload;
    },
    updateShowOtaAcceptAndCreateBackupProcessModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaAcceptAndCreateBackupProcessModal.showOtaAcceptAndCreateBackupProcessModal =
        action.payload;
    },
    updateShowOtaInstallationInProgressModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaInstallationInProgressModal.showOtaInstallationInProgressModal =
        action.payload;
    },
    updateShowOtaInstallationCompleteModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaInstallationCompleteModal.showOtaInstallationCompleteModal =
        action.payload;
    },
    updateShowOtaInstallationFailedModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.otaInstallationFailedModal.showOtaInstallationFailedModal =
        action.payload;
    },
  },
});

export const {
  updateShowRenderOnlyForDevelopersStyle,
  updateShowRenderUnreleasedFeaturesStyle,
  updateShowAddMachineGroupsAdminPanelModal,
  updateScanIdForInProgressScan,
  updateShowScanModal,
  updateScanModalState,
  updateWarnScanModalState,
  updateScanInterruptionModalState,
  updateConnectedServerListData,
  updateDisconnectedServerListData,
  updateConnectionView,
  updateServerListsResultsReady,
  updateGatewaysWithSourcesResultsReady,
  updateUserListResultsReady,
  updateShowSaveBlueprintModal,
  updateBlueprintModalContentShown,
  updateSelectedBlueprint,
  updateShowAutoMatchBlueprintModal,
  updateAutoMatchBlueprintModalContentShown,
  updateSelectedAutoMatchBlueprint,
  updateShowMyBlueprintLibraryModal,
  updateMyBlueprintLibraryModalContentShown,
  updateSelectedMyBlueprintLibraryBlueprint,
  updateShowFindBlueprintModal,
  updateFindBlueprintModalContentShown,
  updateSelectedFindBlueprint,
  updateShowUnsavedStructureModal,
  updateShowUnsavedStructureForNodeManagerNavigationModal,
  updateActiveViewUnsavedStructureForNodeManagerNavigationModal,
  updateShowUnsavedConditionMonitoringForNodeManagerNavigationModal,
  updateActiveViewUnsavedConditionMonitoringForNodeManagerNavigationModal,
  updateShowOverwriteStructureWarningModal,
  updateEditStructurePageInitialData,
  updateEditStructurePageInitialDataFiltered,
  updateEditStructurePageTransferData,
  updateEditStructurePageTransferDataFiltered,
  updateUnassignedBlueprintNodeDataFiltered,
  updateSelectedAndDraggedNodeData,
  updateStructureWasSaved,
  updateShowInitialFilterModal,
  updateShowMachineStructureFilterModal,
  updateInitialFilterModalFilterInputContent,
  updateMachineStructureFilterModalFilterInputContent,
  updateMaximumNumberOfMonitoredItems,
  updateShowMqttModal,
  updateClientConfiguration,
  updateMqttConfiguration,
  updateStateMqttModal,
  updateErrorsMqttModal,
  updateShowRemoveMqttCheckModal,
  updateNodeSelectionState,
  updateNodeSelectionNodeData,
  updateSnackbarNotification,
  updateNodeSelectionNodeEditedState,
  updateAlarmNodeSelectionData,
  updateAlarmNodeSelectionState,
  updateNodeManagerActiveView,
  updateUserManagementListActiveView,
  updateActiveMachineStructureNode,
  updateActiveInitialStructureNode,
  updateClientIds,
  updateNamespace,
  updateDeleteIconActiveState,
  updateAuthContext,
  updateShowDropAreaDeactivationOverlay,
  updateShowMachineStructureDropAreaDeactivationOverlay,
  updateShowBulkEditDataAcquisitionTypeModal,
  updateShowBulkEditDataAcquisitionTypeIcon,
  updateIsOneClickSolutionActive,
  updateIsBlueprintApplied,
  updateUpdatedBlueprintInfo,
  updateLicensedConfigurationsLoading,
  updateLicensedConfigurationsLoaded,
  updateLicensedConfigurationsError,
  updateCustomerInformationForSupportTicket,
  updateModalsCloseDrawerModal,
  updateMachineInformationForRequestRemoteAccessDrawerModal,
  updateAddRequestTechnicalSupportDrawerModal,
  updateAddRequestTechnicalSupportDrawerModalWithoutMachine,
  updateEditWidgetDrawerModalTileId,
  updateGlobalLookForLastTimeStampBeforeFetchingData,
  updateConnectedGatewaysListData,
  updateDisconnectedGatewaysListData,
  updateDuplicateStructureIds,
  updateLanguage,
  updateTimezone,
  updateCurrentlySelectedUserForEditUserDrawer,
  updateCurrentlySelectedServiceUserForEditUserDrawer,
  updateAllMachinesListData,
  updateSearchMachinesListData,
  updateMachinesManagerSearchTerms,
  updateMachinesManagerSearchQuery,
  updateMachinesManagerShowSearchBar,
  updateUserManagementSearchTerms,
  updateUserManagementSearchQuery,
  updateIsAIRagChatOpen,
  updateRolesDescriptions,
  updateOTAActiveView,
  updateSupportTicketInformation,
  updateSupportCommentsByTicket,
  updateStreamingAiResponseText,
  updateStreamingAiResponseTools,
  updateIsStreamingAiResponse,
  updateStreamingTicketId,
  updateStreamingTicketAttachments,
  resetStreamingAiResponse,
  updateShowFirmwareUploadModal,
  updateFirmwareUploadModalState,
  updateFirmwareUploadModalMessage,
  updateShowFirmwareInstallModal,
  updateShowConfigurationUploadModal,
  updateConfigurationUploadModalState,
  updateConfigurationUploadModalMessage,
  updateShowConfigurationInstallModal,
  updateShowOtaDownloadModal,
  updateShowConfigurationBackupInstallModal,
  updateShowFirmwareBackupInstallModal,
  updateShowOtaPushFirmwareModal,
  updateShowOtaCreateBackupModal,
  updateShowOtaConfirmFirmwareInstallationModal,
  updateShowOtaConfirmBackupProcessModal,
  updateShowOtaConfirmFirmwareBackupInstallationModal,
  updateShowOtaPushFirmwareBackupModal,
  updateShowOtaAcceptAndPushFirmwareModal,
  updateShowOtaAcceptAndCreateBackupProcessModal,
  updateShowOtaInstallationInProgressModal,
  updateShowOtaInstallationCompleteModal,
  updateShowOtaInstallationFailedModal,
} = yourSlice.actions;

export default yourSlice.reducer;
